import { GetCheckInModalDataResponse } from '@/backend/features/missions/shared/schemas/getCheckInModalDataSchema';
import { BadgeAlert } from '@/ui/features/missions/components/BadgeAlert';
import { BadgeCheckInModalMissionBlock } from '@/ui/features/missions/components/BadgeCheckInModalMissionBlock';
import { BadgeCheckInModalUserBlock } from '@/ui/features/missions/components/BadgeCheckInModalUserBlock';
import { CheckInVolunteerParams } from '@juvare/missions/backend/features/shift/schemas/checkInVolunteerSchema';
import { CheckOutVolunteerParams } from '@juvare/missions/backend/features/shift/schemas/checkOutVolunteerSchema';
import { Box, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BaseBadgeCheckInModalProps {
    reloadData: () => void;
    handleCheckInVolunteer: (params: CheckInVolunteerParams) => Promise<void>;
    handleCheckOutVolunteer: (params: CheckOutVolunteerParams) => Promise<void>;
    close: (closed?: () => void) => void;
    onCloseModal: () => void;
    onSuccess: () => void;
}

type BadgeCheckInModalProps = BaseBadgeCheckInModalProps &
    (
        | {
              data: GetCheckInModalDataResponse;
              error?: string;
          }
        | {
              data?: GetCheckInModalDataResponse;
              error: string;
          }
    );

export const BadgeCheckInModal: React.FC<BadgeCheckInModalProps> = ({
    data,
    error,
    close,
    reloadData,
    handleCheckOutVolunteer,
    handleCheckInVolunteer,
    onCloseModal,
    onSuccess,
}) => {
    const { t } = useTranslation('rms');

    const ariaTitleId = React.useId() + '-badge-check-in';
    const ariaContentId = React.useId() + '-badge-content-check-in';

    if (!data || error) {
        return (
            <>
                <DialogTitle id={ariaTitleId}>{t('pages.home.checkInModal.titleNotFound')}</DialogTitle>
                <DialogContent id={ariaContentId}>{t('pages.home.checkInModal.notFound')}</DialogContent>
                <DialogActions>
                    <Button color='primary' onClick={() => onCloseModal()}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </>
        );
    }

    return (
        <>
            <DialogTitle id={ariaTitleId}>{t('pages.home.checkInModal.title')}</DialogTitle>
            <DialogContent id={ariaContentId}>
                <BadgeAlert
                    badgeTitle={data.badgeTitle || ''}
                    badgeOrganization={data.accountBadgeOrganization}
                    accountBadgeStatus={data.accountBadgeStatus}
                />
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        gap: 2,
                        mt: 2,
                        pr: 0,
                        width: '100%',
                        [theme.breakpoints.down('md')]: { flexWrap: 'wrap' },
                    })}
                >
                    <BadgeCheckInModalUserBlock data={data} />
                    <BadgeCheckInModalMissionBlock data={data} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color='primary' onClick={() => onCloseModal()}>
                    {t('common.cancel')}
                </Button>

                <BadgeCheckInModalPrimaryActions
                    data={data}
                    reloadData={reloadData}
                    onCloseModal={onCloseModal}
                    close={close}
                    handleCheckInVolunteer={handleCheckInVolunteer}
                    handleCheckOutVolunteer={handleCheckOutVolunteer}
                    onSuccess={onSuccess}
                />
            </DialogActions>
        </>
    );
};

const BadgeCheckInModalPrimaryActions: React.FC<BaseBadgeCheckInModalProps & { data: GetCheckInModalDataResponse }> = ({
    data,
    onCloseModal,
    onSuccess,
    handleCheckOutVolunteer,
    handleCheckInVolunteer,
    close,
    reloadData,
}) => {
    const { t } = useTranslation('rms');

    return (
        <>
            {!data.shiftCheckInTime && !data.shiftAttendanceStatus && (
                <Button
                    color='primary'
                    onClick={() => {
                        handleCheckInVolunteer({ participationId: data.shiftParticipantId }).then(() => {
                            reloadData();
                            onCloseModal();
                            close();

                            onSuccess();
                        });
                    }}
                >
                    {t('pages.home.checkInModal.checkIn')}
                </Button>
            )}

            {data.shiftCheckInTime && data.shiftAttendanceStatus === 'checkedIn' && (
                <Button
                    color='primary'
                    onClick={() => {
                        handleCheckOutVolunteer({ participationId: data.shiftParticipantId }).then(() => {
                            reloadData();
                            onCloseModal();
                            close();

                            onSuccess();
                        });
                    }}
                >
                    {t('pages.home.checkInModal.checkOut')}
                </Button>
            )}
        </>
    );
};
